import {
  Badge,
  Group,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlaeneList } from '@/api/usePlaene';
import { useVergleich } from '@/api/useVergleich';
import { useAppStore } from '@/store/appStore';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';

function formatNumber(value: number | null, locale: string): string {
  if (value === null) return '—';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

export function VergleichPage() {
  const { t, i18n } = useTranslation(['vergleich', 'common']);
  const einrichtungId = useAppStore((s) => s.activeEinrichtungId);
  const jahr = useAppStore((s) => s.activeFoerderjahr);

  const filters = useMemo(
    () => ({
      einrichtung_id: einrichtungId ?? undefined,
      foerderjahr: jahr,
    }),
    [einrichtungId, jahr],
  );

  const plaeneQuery = usePlaeneList(filters);
  const [planA, setPlanA] = useState<string | null>(null);
  const [planB, setPlanB] = useState<string | null>(null);
  const [diffOnly, setDiffOnly] = useState(false);
  const diff = useVergleich(planA, planB);

  const planOptions = useMemo(
    () =>
      (plaeneQuery.data ?? []).map((p) => ({
        value: p.id,
        label: `${p.name} (${p.foerderjahr})`,
      })),
    [plaeneQuery.data],
  );

  return (
    <Stack>
      <Title order={2}>{t('vergleich:title')}</Title>

      <Paper withBorder p="md">
        <Group>
          <Select
            label={t('vergleich:select.planA')}
            placeholder={t('vergleich:select.placeholder')}
            data={planOptions}
            value={planA}
            onChange={setPlanA}
            searchable
            w={280}
          />
          <Select
            label={t('vergleich:select.planB')}
            placeholder={t('vergleich:select.placeholder')}
            data={planOptions}
            value={planB}
            onChange={setPlanB}
            searchable
            w={280}
          />
          <Switch
            label={t('vergleich:filter.unterschiedeOnly')}
            checked={diffOnly}
            onChange={(e) => setDiffOnly(e.currentTarget.checked)}
          />
        </Group>
      </Paper>

      {!planA || !planB ? (
        <EmptyState title={t('vergleich:leer.title')} description={t('vergleich:leer.message')} />
      ) : planA === planB ? (
        <ErrorState error={t('vergleich:warnung.gleicherPlan')} />
      ) : diff.isLoading ? (
        <LoadingState />
      ) : diff.isError ? (
        <ErrorState error={diff.error} onRetry={() => void diff.refetch()} />
      ) : diff.data ? (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={700}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('vergleich:table.col.kennzahl')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>
                    {t('vergleich:table.col.planA')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>
                    {t('vergleich:table.col.planB')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>
                    {t('vergleich:table.col.diff')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>
                    {t('vergleich:table.col.diffProzent')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {diff.data.rows
                  .filter((r) => !diffOnly || (r.diff_abs !== null && r.diff_abs !== 0))
                  .map((r) => (
                    <Table.Tr key={`${r.section}-${r.key}`}>
                      <Table.Td>
                        <Group gap="xs">
                          <Badge variant="light" color="gray">
                            {r.section}
                          </Badge>
                          {r.label}
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatNumber(r.value_a, i18n.language)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatNumber(r.value_b, i18n.language)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {formatNumber(r.diff_abs, i18n.language)}
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        {r.diff_pct !== null
                          ? `${formatNumber(r.diff_pct * 100, i18n.language)} %`
                          : '—'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ) : null}
    </Stack>
  );
}
